import { resolveWorkflowCommand, type DraftingStreamOptions } from './ai';
import {
  composeDraftingInstruction,
  resolveDraftingFormattingBundle,
} from './formattingExemplar';
import { isDocumentBlank } from './draftingWorkflow/insertion';
import { matchDraftDocumentRequest } from './draftingWorkflow/catalog';
import type { DraftChangeScope, LexCounselWorkspaceSnapshot } from '../store/lexCounselStore';

/** True when the workspace holds a complete, apply-ready selection snapshot. */
export function hasValidWorkspaceSelection(workspace: LexCounselWorkspaceSnapshot): boolean {
  const text = workspace.selectedText?.trim();
  const from = workspace.selectionFrom;
  const to = workspace.selectionTo;
  return Boolean(text && from != null && to != null && from < to);
}

/** Freeze selection fields so later store updates cannot invalidate an in-flight request. */
export function snapshotWorkspaceForDrafting(
  workspace: LexCounselWorkspaceSnapshot,
): LexCounselWorkspaceSnapshot {
  if (!hasValidWorkspaceSelection(workspace)) return { ...workspace };
  return {
    ...workspace,
    selectedText: workspace.selectedText,
    selectedHtml: workspace.selectedHtml,
    selectionFrom: workspace.selectionFrom,
    selectionTo: workspace.selectionTo,
  };
}

function isDraftQuestion(text: string): boolean {
  const q = text.trim().toLowerCase();
  if (q.endsWith('?')) return true;
  return /^(what|why|how|when|where|who|is|are|can|should|does|do|explain|tell me|summarize|describe)/.test(
    q,
  );
}

/** Full document/application generation (not a Q&A or single-paragraph tweak). */
export function isDocumentGenerationRequest(
  text: string,
  caseData?: Record<string, unknown> | null,
): boolean {
  const q = text.trim().toLowerCase();
  if (!q || isDraftQuestion(text)) return false;

  if (matchDraftDocumentRequest(text, caseData)) return true;

  const hasAction =
    /\b(write|draft|create|prepare|generate|compose|make|draw up|file|need|give me|help me|help with|type|develop|build|put together|start|begin|open|frame|pen|do)\b/.test(
      q,
    );
  const hasDocType =
    /\b(application|plaint|petition|written statement|written reply|reply|replication|rejoinder|affidavit|appeal|memo|memorandum|suit|complaint|notice|demand|bail|vakalatnama|caveat|objection|counter|affidavit|synopsis|challan|i\.?\s*a\.?|interim application|stay|injunction|revision|review|reference|execution|vakalat|power of attorney|legal notice|plaintiff|defendant)\b/.test(
      q,
    );

  // "draft application", "application for stay", "need a plaint", etc.
  return hasAction || hasDocType;
}

/** Document-wide structural/formatting fixes (numbering, captions, etc.) — not single-paragraph edits. */
export function isDocumentStructuralEditRequest(text: string): boolean {
  const q = text.trim().toLowerCase();
  if (!q || isDraftQuestion(text)) return false;

  if (
    /\b(numbering|numbered|paragraph numbers?|list numbers?|restart numbering|continue numbering|renumber|re-number)\b/.test(
      q,
    )
  ) {
    return true;
  }

  if (
    /\b(this|the|whole|entire|full)\s+(document|draft|pleading|petition|plaint|application)\b/.test(q) &&
    /\b(fix|correct|update|repair|adjust|reformat|format|clean up|clean-up|standardize|standardise|restart|continue|renumber)\b/.test(
      q,
    )
  ) {
    return true;
  }

  return false;
}

/** Broad corrections belong to the open document, never a newly generated tab. */
export function isDocumentWideCorrectionRequest(text: string): boolean {
  const q = text.trim().toLowerCase();
  if (!q || isDraftQuestion(text)) return false;

  const hasWideScope =
    /\b(everywhere|throughout|all occurrences?|entire (?:document|draft|pleading)|whole (?:document|draft|pleading)|globally)\b/.test(
      q,
    );
  const hasEditVerb =
    /\b(correct|fix|replace|update|change|standardi[sz]e|amend|revise|rename)\b/.test(q);
  const hasLegalEntity =
    /\b(lawyer|advocate|counsel|party|petitioner|respondent|plaintiff|defendant|court|judge|case number|citation|date|prayer|signature)\b/.test(
      q,
    );

  return hasEditVerb && (hasWideScope || hasLegalEntity);
}

export function inferDraftScope(
  workspace: LexCounselWorkspaceSnapshot,
  explicit?: DraftChangeScope,
  userPrompt?: string,
): DraftChangeScope {
  if (explicit) return explicit;
  if (hasValidWorkspaceSelection(workspace)) return 'selection';
  if (isDocumentBlank(workspace.draftText ?? '')) return 'document';
  if (userPrompt && isDocumentWideCorrectionRequest(userPrompt)) return 'document';
  if (userPrompt && isDocumentGenerationRequest(userPrompt, workspace.caseData)) return 'new_tab';
  if (userPrompt && isDocumentStructuralEditRequest(userPrompt)) return 'document';
  return 'paragraph';
}

export async function buildUnifiedDraftingRequest(input: {
  workspace: LexCounselWorkspaceSnapshot;
  userPrompt: string;
  scope: DraftChangeScope;
  commandId?: string;
  selectedText?: string;
  counselAskMode?: boolean;
}): Promise<{
  instruction: string;
  templateOptions?: DraftingStreamOptions;
}> {
  const { workspace, userPrompt, scope, commandId, selectedText, counselAskMode } = input;
  const draftHtml = workspace.draftText ?? '';
  const documentTitle = workspace.draftTitle?.trim() || 'Draft';
  let taskInstruction = userPrompt.trim();

  const tabCtx = {
    activeTabId: workspace.activeTabId,
    draftTabs: workspace.draftTabs as never,
  };

  if (
    !counselAskMode &&
    (scope === 'document' || scope === 'new_tab' || scope === 'insert' || scope === 'paragraph' || scope === 'selection')
  ) {
    try {
      const resolved = await resolveWorkflowCommand({
        documentTitle,
        draftContent: draftHtml,
        caseData: workspace.caseData,
        selectedText: selectedText ?? workspace.selectedText ?? '',
        commandId,
        query: commandId ? undefined : userPrompt,
        ...tabCtx,
      });
      taskInstruction = resolved.instruction;
    } catch {
      // Keep raw prompt when workflow resolution fails.
    }

    const sel = selectedText?.trim() || workspace.selectedText?.trim();
    if (scope === 'selection' && sel) {
      taskInstruction = `${taskInstruction}\n\nSelected text to revise:\n${sel}`;
    } else if (scope === 'paragraph' && sel && !commandId) {
      taskInstruction = `${taskInstruction}\n\nContext selection:\n${sel}`;
    } else if (scope === 'insert' || scope === 'new_tab') {
      taskInstruction = `${taskInstruction}\n\nGenerate the complete requested document as HTML. Output the full new document only — a separate blank tab will receive this draft. Do not rewrite or repeat text from other open tabs.`;
    }
  }

  let formattingBundle = {};
  if (scope === 'document' || scope === 'new_tab' || scope === 'insert') {
    formattingBundle = resolveDraftingFormattingBundle({
      caseData: workspace.caseData,
      documentTitle,
      intentTitle: userPrompt.slice(0, 120) || documentTitle,
      draftHtml,
      systemTemplates: workspace.formattingSystemTemplates ?? [],
      workspaceTemplates: workspace.formattingWorkspaceTemplates,
      userData: workspace.formattingUserData,
    });
  }

  return {
    instruction: composeDraftingInstruction(taskInstruction, formattingBundle),
    templateOptions: formattingBundle.templateOptions,
  };
}
